import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {AiRedesignPromptBox} from '@Components/ai-resdesign-prompt-box';
import type {EditableComponentsMap, SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent, PromptOption, RedesignIssueType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {isUserPremium} from '@Libraries/user.library';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@/components/pill-button/pill-button.types';
import {IconSize} from '@/components/icon-v2/icon.types';
import styles from './ai-image-prompt-box.module.scss';
import {getCreditCostForModel} from '@Libraries/ai-redesign-prompt-box-library';
import {updateSelectedAiImageModelId, updateSelectedAiImageModelIdBackend} from '@/reducers/user-preferences.reducer';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {getAiImageModelFromAiModel, getAiModelFromAiImageModel, getErrorMessage} from '../../ai-image-panel-library';
import {AiImageModelOption, MediaTypePrompt, TextToImageGridErrorState} from '@Panels/ai-image-panel/ai-image-panel.types';
import {isChristmasSeason} from '@Utils/seasonal-promotion.util';

interface AiImagePromptBoxProps {
  generateImage: (prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues, editableComponentsMap?: EditableComponentsMap) => Promise<void>;
  errorState: TextToImageGridErrorState;
  remainingCredits: number;
  mediaTypePrompt: MediaTypePrompt;
  updateMediaTypePrompt: (mediaType: MediaTypePrompt) => void;
}

export default function AiImagePromptBox({...props}: AiImagePromptBoxProps): ReactElement<HTMLDivElement> {
  const selectedAiModelId = useAppSelector((state) => {
    return state.userPreferences.selectedAiImageModelId;
  });
  const [actionCost, setActionCost] = useState<number>(getCreditCostForModel(getAiModelFromAiImageModel(selectedAiModelId)));
  const dispatch = useAppDispatch();

  const updateSelectedMediaType = (mediaType: MediaTypePrompt): void => {
    props.updateMediaTypePrompt(mediaType);
    if (mediaType !== MediaTypePrompt.PHOTO && selectedAiModelId !== AiImageModelOption.Gemini2_5) {
      dispatch(updateSelectedAiImageModelId(AiImageModelOption.Gemini2_5));
    }
  };

  const onSelectedModelUpdate = (promptOptions: SelectedPromptOptionValues): void => {
      Object.keys(promptOptions).forEach((option) => {
          if (option === PromptOption.AI_MODEL.valueOf()) {
              const updatedModel = getAiImageModelFromAiModel(promptOptions[PromptOption.AI_MODEL]);
              if (updatedModel === selectedAiModelId) {
                  return;
              }
              let creditCost = getCreditCostForModel(promptOptions[PromptOption.AI_MODEL]);
              if (props.mediaTypePrompt !== MediaTypePrompt.PHOTO) {
                  creditCost += 1;
              }
              if (actionCost !== creditCost) {
                setActionCost(creditCost);
              }
              void updateSelectedAiImageModelIdBackend(updatedModel);
              dispatch(updateSelectedAiImageModelId(updatedModel));
              if (props.mediaTypePrompt !== MediaTypePrompt.PHOTO && updatedModel !== AiImageModelOption.Gemini2_5) {
                  props.updateMediaTypePrompt(MediaTypePrompt.PHOTO);
              }
          }
      });
  }

  const getPlaceHolders = (): string[] => {
    if (isChristmasSeason()) {
      return [
        'Try "Polar bear in a Santa hat"',
        'Try "Pile of wrapped gifts with a FREE SHIPPING banner laid across them"',
        'Try "Snow globe containing a tiny, bustling Christmas market at night."',
        'Try "Cat wearing an ugly Christmas sweater,"',
      ];
    }
    if (props.mediaTypePrompt === MediaTypePrompt.LOGO) {
      return [
        'Try “Create a minimalist logo for a cafe named “Beans””',
        'Try “Abstract logo for "Bloom Florist", light green”',
        'Try “Mascot logo for a high school team named "The Grizzlies"”',
        'Try “Bold text logo for "Urban Beats Studio", neon colors”',
      ];
    }
    if (props.mediaTypePrompt === MediaTypePrompt.STICKER) {
      return [
        'Try “Create an anime style sticker of a red mushroom with polka dots”',
        '"Happy Birthday" sticker, cursive, vibrant colors.',
        'Cartoonish sticker of a cat wearing sunglasses.',
        'Retro sticker: "Good Vibes Only", pastel colors',
      ];
    }
    return ['Witch flying past a full moon...'];
  };

  const getAdditionalPromptRow = (): ReactElement<any> => {
    return (
      <div className={`flex-row-align-center ${styles.promptPills}`}>
        <PillButton
          isSelected={props.mediaTypePrompt === MediaTypePrompt.PHOTO}
          size={PillButtonSize.SMALL}
          leftIconProps={{
            icon: 'icon-image',
            size: IconSize.SIZE_ICON_16,
          }}
          text={window.i18next.t('pmwjs_photo')}
          onClick={(_) => {
            updateSelectedMediaType(MediaTypePrompt.PHOTO);
          }}
        />
        <PillButton
          isSelected={props.mediaTypePrompt === MediaTypePrompt.LOGO}
          size={PillButtonSize.SMALL}
          leftIconProps={{
            icon: 'icon-designs',
            size: IconSize.SIZE_ICON_16,
          }}
          text={window.i18next.t('pmwjs_logo')}
          onClick={(_) => {
            updateSelectedMediaType(MediaTypePrompt.LOGO);
          }}
        />
        <PillButton
          isSelected={props.mediaTypePrompt === MediaTypePrompt.TEXT_ART}
          size={PillButtonSize.SMALL}
          leftIconProps={{
            icon: 'icon-fancy-text',
            size: IconSize.SIZE_ICON_16,
          }}
          text={window.i18next.t('pmwjs_text_art')}
          onClick={(_) => {
            updateSelectedMediaType(MediaTypePrompt.TEXT_ART);
          }}
        />
        <PillButton
          isSelected={props.mediaTypePrompt === MediaTypePrompt.STICKER}
          size={PillButtonSize.SMALL}
          leftIconProps={{
            icon: 'icon-sticker',
            size: IconSize.SIZE_ICON_16,
          }}
          text={window.i18next.t('pmwjs_sticker')}
          onClick={(_) => {
            updateSelectedMediaType(MediaTypePrompt.STICKER);
          }}
        />
      </div>
    );
  };

  const shouldShowRetryButton = (): boolean => {
    return props.errorState === TextToImageGridErrorState.GENERIC;
  };

  const getPromptBoxPlaceholder = (): string => {
    if (isChristmasSeason()) {
      const christmasPlaceholders = getPlaceHolders();
      const index = Math.floor(Math.random() * christmasPlaceholders.length);
      return christmasPlaceholders[index];
    }
    return 'Witch flying past a full moon...';
  };

  return (
    <>
      <AiRedesignPromptBox
        promptBoxClasses={'spacing-p-r-3'}
        regenerateContent={props.generateImage}
        placeholder={getPromptBoxPlaceholder()}
        editableComponentsMap={{
          [EditableComponent.IMAGE]: true,
        }}
        promptOptionsForEditableComponents={{
          [EditableComponent.IMAGE]: [PromptOption.STYLES, PromptOption.ASPECT_RATIO, PromptOption.AI_MODEL],
        }}
        defaultSelectedPromptOptionValues={{
          [PromptOption.AI_MODEL]: getAiModelFromAiImageModel(selectedAiModelId),
        }}
        isEditableDuringGeneration
        redesignIssueOnRender={
          props.errorState !== TextToImageGridErrorState.NONE
            ? {
                type: RedesignIssueType.ERROR,
                icon: 'icon-warning',
                message: getErrorMessage(props.errorState),
                showRetryButton: shouldShowRetryButton(),
              }
            : undefined
        }
        upsellDialog={{
          dialogName: window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_IMAGE as string,
          showDialog: !isUserPremium(),
        }}
        unselectOnOutsideClick
        isPromptBoxSelectedOnRender
        onSelectedPromptOptionValuesChange={onSelectedModelUpdate}
        creditsPillContent={
          props.remainingCredits
            ? {
                remainingCredits: props.remainingCredits,
                actionCost: actionCost,
              }
            : undefined
        }
      />
      {/*{getAdditionalPromptRow()}*/}
    </>
  );
}
